import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/i18n/format';
import styles from './topics-donut.module.scss';
import Legend from '@/components/legend/legend';
import { LegendItem } from '@/components/legend/legend.types';
import { Insight } from '@/services/dashboard-service/dashboard.service.types';
import { toDonutSegments } from './topics-donut.utils';

interface TopicsDonutProps {
  topics: Insight;
  className?: string;
}

export default function TopicsDonut({ topics, className }: TopicsDonutProps) {
  const { t } = useTranslation('metrics');
  const segments = toDonutSegments(topics);

  if (!segments.length) return null;

  const total = segments.reduce((sum, segment) => sum + segment.count, 0);

  // Hard stops, clockwise from twelve. Unrounded so the ring closes exactly, while
  // the legend rounds and so may not read as summing to a hundred.
  let sweep = 0;
  const stops = segments.map((segment) => {
    const from = sweep;
    sweep += segment.percent;

    return `${segment.color} ${from}% ${sweep}%`;
  });

  const items: LegendItem[] = segments.map((segment) => ({
    label: segment.label,
    color: segment.color,
    value: `${Math.round(segment.percent)}%`,
  }));

  return (
    <div className={clsx(styles.wrap, className)}>
      <div className={styles.donut} style={{ background: `conic-gradient(${stops.join(',')})` }}>
        <div className={styles.mid}>
          <b className={styles.total}>{formatNumber(total)}</b>
          {/* Not "calls": the counts are topic taggings, and one call can carry
              several, so this total legitimately exceeds the call count. */}
          <span className={styles.unit}>{t('topicsDonut.mentions', { count: total })}</span>
        </div>
      </div>
      <Legend className={styles.legend} items={items} orientation="vertical" />
    </div>
  );
}
